import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type {
  ChatCitation,
  ChatConfigResponse,
  ChatMessageRequest,
  ChatSessionTab,
  ChatStreamEvent,
} from '@lp/shared';
import {
  Brain,
  Check,
  ChevronRight,
  Circle,
  CircleDot,
  ExternalLink,
  EyeOff,
  Loader2,
  Send,
  Sparkles,
  Square,
  X,
} from 'lucide-react';
import { ApiError, streamEvents } from '../../lib/api.js';
import { formatDuration } from '../../lib/format.js';
import { Markdown } from '../Markdown.js';
import { Alert, Badge, Button, LoadingPane, Textarea, clsx } from '../ui.js';
import { useChatConfig } from '../../lib/useLearnerData.js';

/**
 * The AI panel.
 *
 * Two contracts govern this component and both are visible in the UI rather
 * than buried in a tooltip:
 *
 *  1. **Nothing is saved.** The thread is React state and only React state —
 *     no localStorage, no sessionStorage, no IndexedDB, no query cache, no
 *     server table. Prior turns travel in the request body precisely so the
 *     server can stay stateless about them, and unmounting this component (a
 *     reload, a navigation, closing the drawer's parent page) is what ends the
 *     conversation — and aborts a turn still in flight. The header says so
 *     permanently.
 *  2. **A stub says it is a stub.** When `mode === 'stub'` the notice sits at
 *     the top of the thread and every assistant turn is framed as a pointer to
 *     material, not as an answer — because it is not one.
 *
 * With a model attached, a turn streams in: what the assistant is doing (its
 * reasoning when the model shares it, each tool call, its plan) shows as an
 * activity trail that folds away once the answer is in, and the answer's
 * citations are links that seek the video, open a tab, or go to another
 * session.
 *
 * The two fields of `/api/chat/config` are not the same question and are not
 * treated as one here: `enabled` is whether the panel works at all, and `mode`
 * is whether a model is attached behind it. A page decides whether to mount
 * this component from its own `chatEnabled`; this component still checks
 * `enabled`, so an operator switching the panel off is met with an explanation
 * rather than a composer whose every message would be refused.
 */

type Step =
  | { kind: 'reasoning'; text: string }
  | { kind: 'interim'; text: string }
  | {
      kind: 'tool';
      id: string;
      name: string;
      label: string;
      status: 'running' | 'done' | 'failed';
      preview: string | null;
    }
  | {
      kind: 'todos';
      items: { content: string; status: 'pending' | 'in_progress' | 'completed' }[];
    };

type UserTurn = { id: number; role: 'user'; content: string };
type AssistantTurn = {
  id: number;
  role: 'assistant';
  /** The final reply once `done`; until then, the text streamed so far. */
  content: string;
  citations: ChatCitation[];
  steps: Step[];
  streaming: boolean;
  /** Assistant turns produced by the unwired stub are labelled as such. */
  stub: boolean;
  error: string | null;
  startedAt: number;
  finishedAt: number | null;
};
type Turn = UserTurn | AssistantTurn;

let turnId = 0;

/** Citation markers the server resolves at the end, hidden while text streams. */
const PENDING_MARKER = /\s?\[\[[^\]]*\]\]|\s?\[\[[^\]]*$/g;

export function ChatPanel({
  scopeType,
  scopeId,
  scopeLabel,
  onSeek,
  onOpenTab,
  headerAction,
  className,
}: {
  scopeType: 'session' | 'capstone';
  scopeId: string;
  /** What the assistant is scoped to, named in the empty state. */
  scopeLabel: string;
  /** Present on the session page: a citation can jump the video. */
  onSeek?: (sec: number) => void;
  /** Present on the session page: a citation can open the tab that holds a material. */
  onOpenTab?: (tab: ChatSessionTab) => void;
  /** A control for the header's top-right corner — the session page's collapse button. */
  headerAction?: ReactNode;
  className?: string;
}) {
  const { data: config, isPending, error } = useChatConfig(true);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const inputId = useId();
  const statusId = useId();

  // Leaving the page ends the conversation; it also ends a turn in flight.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Follow the newest text while the reader is at the bottom; leave them be
  // if they scrolled up to reread something.
  useEffect(() => {
    const el = threadRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [turns]);

  const update = (id: number, fn: (t: AssistantTurn) => AssistantTurn): void =>
    setTurns((prev) => prev.map((t) => (t.id === id && t.role === 'assistant' ? fn(t) : t)));

  const apply = (id: number, e: ChatStreamEvent): void =>
    update(id, (t) => {
      switch (e.type) {
        case 'text':
          return { ...t, content: t.content + e.delta };
        case 'reasoning': {
          const last = t.steps.at(-1);
          // Text written before a step is the assistant narrating its way, not the answer.
          const steps = t.content ? [...t.steps, { kind: 'interim' as const, text: t.content }] : t.steps;
          if (!t.content && last?.kind === 'reasoning') {
            return { ...t, steps: [...t.steps.slice(0, -1), { kind: 'reasoning', text: last.text + e.delta }] };
          }
          return { ...t, content: '', steps: [...steps, { kind: 'reasoning', text: e.delta }] };
        }
        case 'tool_start': {
          const steps = t.content ? [...t.steps, { kind: 'interim' as const, text: t.content }] : t.steps;
          return {
            ...t,
            content: '',
            steps: [
              ...steps,
              { kind: 'tool', id: e.id, name: e.name, label: e.label, status: 'running', preview: null },
            ],
          };
        }
        case 'tool_end':
          return {
            ...t,
            steps: t.steps.map((s) =>
              s.kind === 'tool' && s.id === e.id
                ? { ...s, status: e.ok ? 'done' : 'failed', preview: e.preview }
                : s,
            ),
          };
        case 'todos': {
          const rest = t.steps.filter((s) => s.kind !== 'todos');
          return { ...t, steps: [...rest, { kind: 'todos', items: e.items }] };
        }
        case 'done':
          return {
            ...t,
            content: e.response.reply,
            citations: e.response.citations,
            stub: e.response.mode === 'stub',
            streaming: false,
            finishedAt: Date.now(),
          };
        case 'error':
          return { ...t, streaming: false, error: e.message, finishedAt: Date.now() };
      }
    });

  const submit = async (): Promise<void> => {
    const message = draft.trim();
    if (!message || busy) return;

    const history: ChatMessageRequest['history'] = turns
      .filter((t) => t.role === 'user' || (!t.error && t.content))
      .slice(-20)
      .map((t) => ({ role: t.role, content: t.content.slice(0, 8_000) }));

    const assistantId = (turnId += 2);
    setDraft('');
    setSendError(null);
    setBusy(true);
    stickToBottom.current = true;
    setTurns((prev) => [
      ...prev,
      { id: assistantId - 1, role: 'user', content: message },
      {
        id: assistantId,
        role: 'assistant',
        content: '',
        citations: [],
        steps: [],
        streaming: true,
        stub: false,
        error: null,
        startedAt: Date.now(),
        finishedAt: null,
      },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamEvents<ChatStreamEvent>(
        '/chat/stream',
        { scopeType, scopeId, message, history },
        (e) => apply(assistantId, e),
        controller.signal,
      );
      // A stream that closed without `done` or `error` was cut off.
      update(assistantId, (t) =>
        t.streaming ? { ...t, streaming: false, error: 'The answer was cut off.', finishedAt: Date.now() } : t,
      );
    } catch (err) {
      if (controller.signal.aborted) {
        update(assistantId, (t) => ({ ...t, streaming: false, error: 'Stopped.', finishedAt: Date.now() }));
      } else {
        // Refused before it started: the question never reached the assistant.
        setTurns((prev) => prev.filter((t) => t.id !== assistantId && t.id !== assistantId - 1));
        setDraft(message);
        setSendError(
          err instanceof ApiError
            ? (err.reason ?? err.message)
            : 'The assistant could not be reached. Try again in a moment.',
        );
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
  };

  if (isPending) {
    return (
      <div className={clsx('flex flex-col bg-surface border border-line rounded-card', className)}>
        {headerAction ? <div className="flex justify-end p-2 pb-0">{headerAction}</div> : null}
        <LoadingPane label="Opening the assistant…" />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className={clsx('p-4 bg-surface border border-line rounded-card', className)}>
        {headerAction ? <div className="flex justify-end -mt-2 -mr-2 mb-2">{headerAction}</div> : null}
        <Alert tone="warn" title="The assistant is not available">
          {error instanceof ApiError ? error.message : 'The assistant could not be reached.'} The
          rest of this page works normally.
        </Alert>
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className={clsx('p-4 bg-surface border border-line rounded-card', className)}>
        {headerAction ? <div className="flex justify-end -mt-2 -mr-2 mb-2">{headerAction}</div> : null}
        <Alert tone="warn" title="The assistant is switched off here">
          This deployment has the chat panel turned off, so there is nothing to ask. The session
          notes and the video transcript below the player carry the same material.
        </Alert>
      </div>
    );
  }

  return (
    <section
      className={clsx(
        'flex flex-col min-h-0 bg-surface border border-line rounded-card overflow-hidden',
        className,
      )}
      aria-label="Ask AI about this material"
    >
      <Header config={config} action={headerAction} />

      <div
        ref={threadRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        }}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-4"
        aria-live="polite"
        aria-busy={busy}
        aria-label="Conversation"
      >
        {config.mode === 'stub' ? (
          <Alert tone="warn" title="This assistant is not connected to a model">
            {config.stubNotice}
          </Alert>
        ) : null}

        {turns.length === 0 ? (
          <p className="text-sm text-ink-subtle leading-relaxed">
            Ask about {scopeLabel}
            {config.mode === 'live'
              ? ' — a concept from the lecture, a slide, a reading, or how it connects to the rest of the course.'
              : '.'}{' '}
            Answers stay in this panel and are gone the moment you leave the page.
          </p>
        ) : null}

        {turns.map((turn) =>
          turn.role === 'user' ? (
            <div key={turn.id} className="self-end max-w-[85%]">
              <p className="text-xs text-ink-subtle text-right mb-1">You</p>
              <p className="bg-surface-2 border border-line rounded-control px-3 py-2 text-sm text-ink whitespace-pre-wrap">
                {turn.content}
              </p>
            </div>
          ) : (
            <AssistantMessage
              key={turn.id}
              turn={turn}
              onSeek={onSeek}
              onOpenTab={onOpenTab}
            />
          ),
        )}

        {sendError ? (
          <Alert tone="danger" title="That message did not go through">
            {sendError}
          </Alert>
        ) : null}
      </div>

      <form
        className="border-t border-line p-3 flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label htmlFor={inputId} className="sr-only">
          Ask a question about {scopeLabel}
        </label>
        <Textarea
          id={inputId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          rows={2}
          maxLength={4000}
          aria-describedby={statusId}
          placeholder={turns.length ? 'Ask a follow-up…' : 'Ask about this material…'}
          className="min-h-16"
        />
        <div className="flex items-center justify-between gap-3">
          <p id={statusId} className="text-xs text-ink-subtle">
            {busy ? 'Working on it — you can stop it at any time.' : 'Enter sends · Shift + Enter starts a new line'}
          </p>
          {busy ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              icon={<Square size={12} aria-hidden />}
              onClick={() => abortRef.current?.abort()}
            >
              Stop
            </Button>
          ) : (
            <Button
              type="submit"
              size="sm"
              variant="primary"
              icon={<Send size={14} aria-hidden />}
              disabled={!draft.trim()}
            >
              Send
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}

function Header({ config, action }: { config: ChatConfigResponse; action?: ReactNode }) {
  return (
    <div className="px-4 py-3 border-b border-line bg-ai-wash">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
          <Sparkles size={15} className="text-ai" aria-hidden />
          Ask AI
        </h2>
        {action ? <div className="-my-1 -mr-2 shrink-0">{action}</div> : null}
      </div>
      {/*
        Permanent, not dismissible: the retention promise is the reason a
        learner can ask a naive question here at all.
      */}
      <p className="text-xs text-ink-muted mt-1.5 flex items-start gap-1.5 leading-relaxed">
        <EyeOff size={13} className="mt-0.5 shrink-0" aria-hidden />
        <span>
          {config.retentionNotice} Leaving this page or reloading ends the conversation and it
          cannot be recovered.
        </span>
      </p>
    </div>
  );
}

// ------------------------------------------------------------ assistant turn ---

function AssistantMessage({
  turn,
  onSeek,
  onOpenTab,
}: {
  turn: AssistantTurn;
  onSeek?: (sec: number) => void;
  onOpenTab?: (tab: ChatSessionTab) => void;
}) {
  const byIndex = new Map(turn.citations.map((c, i) => [c.index ?? i + 1, c]));
  const text = turn.streaming ? turn.content.replace(PENDING_MARKER, '') : turn.content;
  const waiting = turn.streaming && !text && !turn.steps.length;

  return (
    <div className="max-w-[96%]">
      <p className="text-xs text-ink-subtle mb-1 flex items-center gap-1.5">
        Assistant
        {turn.stub ? <Badge tone="warn">Stub reply — not a model answer</Badge> : null}
      </p>

      {turn.steps.length ? <Activity turn={turn} /> : null}

      {waiting ? (
        <p className="text-sm text-ink-subtle flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" aria-hidden />
          Looking through the material…
        </p>
      ) : null}

      {text ? (
        <div
          className={clsx(
            'rounded-control px-3 py-2 border',
            // A stub answer is deliberately not dressed as an authority.
            turn.stub
              ? 'bg-surface-2 border-dashed border-line-strong text-ink-muted text-sm leading-relaxed whitespace-pre-wrap'
              : 'bg-surface-2 border-line',
          )}
        >
          {turn.stub ? (
            text
          ) : (
            <Markdown
              compact
              className="chat-answer"
              renderLink={(href, children) => {
                const m = /#cite-(\d+)$/.exec(href);
                if (!m) return undefined;
                const citation = byIndex.get(Number(m[1]));
                if (!citation) return null;
                return (
                  <CitationAction citation={citation} onSeek={onSeek} onOpenTab={onOpenTab} inline>
                    {children}
                  </CitationAction>
                );
              }}
            >
              {text}
            </Markdown>
          )}
          {turn.streaming && text ? (
            <span className="inline-block w-1.5 h-4 align-text-bottom bg-ai/60 animate-pulse ml-0.5" aria-hidden />
          ) : null}
        </div>
      ) : null}

      {turn.error ? (
        <p className="mt-2 text-xs text-danger flex items-center gap-1.5">
          <X size={13} aria-hidden />
          {turn.error}
        </p>
      ) : null}

      {!turn.streaming && turn.citations.length > 0 ? (
        <Citations citations={turn.citations} onSeek={onSeek} onOpenTab={onOpenTab} />
      ) : null}
    </div>
  );
}

/**
 * What the assistant did on the way to its answer, like an agent's transcript:
 * open while it works, folded to one line once the answer is in.
 */
function Activity({ turn }: { turn: AssistantTurn }) {
  const [open, setOpen] = useState<boolean | null>(null);
  const isOpen = open ?? turn.streaming;
  const tools = turn.steps.filter((s) => s.kind === 'tool').length;
  const thought = turn.steps.some((s) => s.kind === 'reasoning');
  const secs = Math.max(1, Math.round(((turn.finishedAt ?? Date.now()) - turn.startedAt) / 1000));
  const summary = turn.streaming
    ? 'Working…'
    : [
        thought ? 'Thought' : null,
        tools ? `used ${tools} tool${tools === 1 ? '' : 's'}` : null,
        `${secs}s`,
      ]
        .filter(Boolean)
        .join(' · ')
        .replace(/^./, (c) => c.toUpperCase());

  return (
    <div className="mb-2 text-xs">
      <button
        type="button"
        onClick={() => setOpen(!isOpen)}
        aria-expanded={isOpen}
        className="inline-flex items-center gap-1 text-ink-subtle hover:text-ink transition-colors"
      >
        <ChevronRight size={13} className={clsx('transition-transform', isOpen && 'rotate-90')} aria-hidden />
        {summary}
      </button>
      {isOpen ? (
        <ol className="mt-1.5 ml-1.5 pl-3 border-l border-line flex flex-col gap-1.5">
          {turn.steps.map((step, i) => (
            <li key={i}>
              <StepView step={step} live={turn.streaming && i === turn.steps.length - 1} />
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

function StepView({ step, live }: { step: Step; live: boolean }) {
  const [expanded, setExpanded] = useState(false);

  if (step.kind === 'reasoning') {
    return (
      <div className="text-ink-subtle">
        <span className="inline-flex items-center gap-1.5 font-medium text-ai">
          <Brain size={12} aria-hidden />
          {live ? 'Thinking…' : 'Thought'}
        </span>
        <p className="mt-0.5 italic leading-relaxed whitespace-pre-wrap line-clamp-6">{step.text}</p>
      </div>
    );
  }

  if (step.kind === 'interim') {
    return <p className="text-ink-muted leading-relaxed whitespace-pre-wrap">{step.text.replace(PENDING_MARKER, '')}</p>;
  }

  if (step.kind === 'todos') {
    return (
      <ul className="flex flex-col gap-0.5">
        {step.items.map((item, i) => (
          <li key={i} className={clsx('flex items-start gap-1.5', item.status === 'completed' ? 'text-ink-subtle line-through' : 'text-ink-muted')}>
            {item.status === 'completed' ? (
              <Check size={12} className="mt-0.5 text-ok shrink-0" aria-hidden />
            ) : item.status === 'in_progress' ? (
              <CircleDot size={12} className="mt-0.5 text-ai shrink-0" aria-hidden />
            ) : (
              <Circle size={12} className="mt-0.5 shrink-0" aria-hidden />
            )}
            {item.content}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => step.preview && setExpanded((v) => !v)}
        className={clsx(
          'inline-flex items-center gap-1.5 text-left',
          step.preview ? 'hover:text-ink cursor-pointer' : 'cursor-default',
          step.status === 'failed' ? 'text-danger' : 'text-ink-muted',
        )}
        aria-expanded={step.preview ? expanded : undefined}
      >
        {step.status === 'running' ? (
          <Loader2 size={12} className="animate-spin text-ai shrink-0" aria-hidden />
        ) : step.status === 'failed' ? (
          <X size={12} className="shrink-0" aria-hidden />
        ) : (
          <Check size={12} className="text-ok shrink-0" aria-hidden />
        )}
        <span>{step.label}</span>
      </button>
      {expanded && step.preview ? (
        <pre className="mt-1 max-h-48 overflow-auto rounded-control bg-surface-2 border border-line p-2 font-mono text-[11px] leading-snug text-ink-muted whitespace-pre-wrap">
          {step.preview}
        </pre>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- citations ---

/**
 * Where a citation goes: the video seeks, a material opens its tab, another
 * session opens its page, a web page opens in a new tab. The stub's retrieval
 * citations carry no `kind` and keep their original behaviour.
 */
function CitationAction({
  citation,
  onSeek,
  onOpenTab,
  inline = false,
  children,
}: {
  citation: ChatCitation;
  onSeek?: (sec: number) => void;
  onOpenTab?: (tab: ChatSessionTab) => void;
  inline?: boolean;
  children: ReactNode;
}) {
  const cls = inline
    ? 'inline-flex items-center justify-center min-w-4 h-4 px-1 mx-0.5 rounded-sm text-[10px] font-semibold align-super bg-ai-wash text-ai border border-ai/30 hover:border-ai no-underline'
    : 'inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-control border border-line bg-surface-2 text-ink-muted hover:text-ink hover:border-line-strong transition-colors duration-150 ease-out';
  const title = citation.label;

  if (citation.startSec !== null && onSeek) {
    const at = citation.startSec;
    return (
      <button type="button" className={cls} title={title} onClick={() => onSeek(at)}>
        {children}
        <span className="sr-only">— jump the video to {formatDuration(at)}</span>
      </button>
    );
  }
  if (citation.kind === 'session' && citation.href) {
    return (
      <Link to={citation.href} className={cls} title={title}>
        {children}
      </Link>
    );
  }
  // A cited link resource opens the page it links to.
  if (citation.href && /^https?:/i.test(citation.href)) {
    return (
      <a href={citation.href} target="_blank" rel="noopener noreferrer" className={cls} title={title}>
        {children}
      </a>
    );
  }
  if (citation.kind === 'material' && citation.tab && onOpenTab) {
    const tab = citation.tab;
    return (
      <button type="button" className={cls} title={title} onClick={() => onOpenTab(tab)}>
        {children}
        <span className="sr-only">— open the {tab} tab</span>
      </button>
    );
  }
  if (citation.anchor) {
    return (
      <a href={`#${citation.anchor}`} className={cls} title={title}>
        {children}
        <span className="sr-only">— go to this section</span>
      </a>
    );
  }
  return (
    <span className={clsx(cls, 'cursor-default')} title={title}>
      {children}
    </span>
  );
}

function Citations({
  citations,
  onSeek,
  onOpenTab,
}: {
  citations: ChatCitation[];
  onSeek?: (sec: number) => void;
  onOpenTab?: (tab: ChatSessionTab) => void;
}) {
  return (
    <ul className="flex flex-wrap gap-1.5 mt-2" aria-label="Where this came from">
      {citations.map((citation, i) => (
        <li key={`${citation.label}-${i}`}>
          <CitationAction citation={citation} onSeek={onSeek} onOpenTab={onOpenTab}>
            {citation.index ? (
              <span className="font-semibold text-ai tabular-nums">{citation.index}</span>
            ) : null}
            {citation.startSec !== null ? (
              <span className="font-mono tabular-nums text-ai">{formatDuration(citation.startSec)}</span>
            ) : null}
            <span className="max-w-52 truncate">
              {citation.kind === 'video' ? 'Video' : citation.label}
            </span>
            {citation.kind === 'material' && citation.href ? (
              <ExternalLink size={11} aria-hidden />
            ) : null}
          </CitationAction>
        </li>
      ))}
    </ul>
  );
}
